import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export interface FilterPill {
    id: string;
    label: string;
    value: string;
    color?: 'default' | 'warning' | 'error';
    /** When true, clicking the pill body switches into an inline edit field.
     *  Commit fires on Enter or blur via `onCommit`. */
    editable?: boolean;
    /** Render the pill in error styling (red) with a warning glyph. */
    invalid?: boolean;
    /** Called when an editable pill is committed (Enter or blur). */
    onCommit?: (nextValue: string) => void;
}

export interface SearchFilterBarProps {
    pills?: FilterPill[];
    onRemovePill?: (id: string) => void;
    placeholder?: string;
    searchText: string;
    onSearchChange: (value: string) => void;
    compact?: boolean;
}

const Bar = styled.div<{ $compact?: boolean }>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: ${({ $compact }) => $compact ? '5px 10px' : '6px 10px'};
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fff;
    cursor: text;
    min-height: ${({ $compact }) => $compact ? '32px' : '36px'};

    &:focus-within {
        border-color: #ccc;
    }
`;

const SearchIcon = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#bbb"
        strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="5" /><path d="M11 11L14 14" />
    </svg>
);

type PillColor = 'default' | 'warning' | 'error';

const pillBg = (color: PillColor): string => {
    switch (color) {
        case 'warning': return '#fef6ee';
        case 'error': return '#fee2e2';
        default: return '#f0f0f0';
    }
};

const pillFg = (color: PillColor): string => {
    switch (color) {
        case 'warning': return '#c4530a';
        case 'error': return '#b91c1c';
        default: return '#555';
    }
};

const pillLabelFg = (color: PillColor): string => {
    switch (color) {
        case 'warning': return '#c4530a';
        case 'error': return '#b91c1c';
        default: return '#888';
    }
};

const pillDismissHover = (color: PillColor): string => {
    switch (color) {
        case 'warning': return '#fde5d0';
        case 'error': return '#fecaca';
        default: return '#e4e4e4';
    }
};

const PillSpan = styled.span<{ $color: PillColor; $clickable?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 4px 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    cursor: ${({ $clickable }) => ($clickable ? 'text' : 'default')};
    background: ${({ $color }) => pillBg($color)};
    color: ${({ $color }) => pillFg($color)};
`;

const PillLabel = styled.span<{ $color: PillColor }>`
    color: ${({ $color }) => pillLabelFg($color)};
    font-size: 11px;
`;

const PillDismiss = styled.span<{ $color: PillColor }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    cursor: pointer;
    color: ${({ $color }) => pillFg($color)};
    font-size: 14px;
    line-height: 1;

    &:hover {
        background: ${({ $color }) => pillDismissHover($color)};
    }
`;

const PillEditInput = styled.input`
    border: none;
    outline: none;
    background: transparent;
    font: inherit;
    color: inherit;
    padding: 0;
    min-width: 60px;
    width: auto;
`;

const Input = styled.input`
    border: none;
    outline: none;
    font-size: 13px;
    color: #333;
    background: transparent;
    flex: 1;
    min-width: 80px;
    font-family: inherit;
    padding: 0;
`;

interface PillViewProps {
    pill: FilterPill;
    onRemove?: (id: string) => void;
}

const PillView: React.FC<PillViewProps> = ({ pill, onRemove }) => {
    const color: PillColor = pill.invalid ? 'error' : (pill.color ?? 'default');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(pill.value);
    const inputRef = useRef<HTMLInputElement>(null);

    // Keep the draft synced with the canonical value when not editing — this
    // way external changes (e.g., toggling a checkbox) are reflected.
    useEffect(() => {
        if (!editing) {
            setDraft(pill.value);
        }
    }, [pill.value, editing]);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    const startEdit = (event: React.MouseEvent) => {
        if (!pill.editable) return;
        event.stopPropagation();
        setDraft(pill.value);
        setEditing(true);
    };

    const commit = () => {
        setEditing(false);
        pill.onCommit?.(draft);
    };

    const cancel = () => {
        setEditing(false);
        setDraft(pill.value);
    };

    return (
        <PillSpan
            $color={color}
            $clickable={pill.editable && !editing}
            onClick={startEdit}
        >
            <PillLabel $color={color}>{pill.label}</PillLabel>
            {pill.invalid && <span aria-hidden="true" style={{ fontSize: 12, lineHeight: 1 }}>⚠</span>}
            {editing ? (
                <PillEditInput
                    ref={inputRef}
                    type="text"
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    onClick={event => event.stopPropagation()}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            commit();
                        } else if (event.key === 'Escape') {
                            event.preventDefault();
                            cancel();
                        }
                    }}
                    onBlur={commit}
                    style={{ width: `${Math.max(draft.length, 2) + 1}ch` }}
                    aria-label={pill.label}
                />
            ) : (
                <span>{pill.value}</span>
            )}
            {onRemove && !editing && (
                <PillDismiss
                    $color={color}
                    onClick={event => { event.stopPropagation(); onRemove(pill.id); }}
                    role="button"
                    aria-label={`Remove ${pill.label}`}
                >
                    ×
                </PillDismiss>
            )}
        </PillSpan>
    );
};

export const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
    pills = [],
    onRemovePill,
    placeholder = 'Search…',
    searchText,
    onSearchChange,
    compact,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const hasPills = pills.length > 0;

    return (
        <Bar $compact={compact} onClick={() => inputRef.current?.focus()}>
            <SearchIcon />
            {pills.map(pill => (
                <PillView key={pill.id} pill={pill} onRemove={onRemovePill} />
            ))}
            <Input
                ref={inputRef}
                type="text"
                value={searchText}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={hasPills ? '' : placeholder}
            />
        </Bar>
    );
};
